import { useState } from 'react'
import { Box } from '@mui/material'
import SecDimButton from '../../shared/SecDimButton/SecDimButton'
import { hasCustomisation } from './drSecDimConfig'
import {
	DEFAULT_CUSTOMISATION,
	WIZARD_STEPS,
	WIZARD_STEP_COUNT,
	isFirstStep,
	isLastStep,
	isStepAnswered,
	nextStep,
	prevStep,
	withDefaults,
	finalisePreferences,
	LANGUAGE_OPTIONS,
	EXPERIENCE_OPTIONS,
	ROLE_OPTIONS,
	OBJECTIVE_OPTIONS,
	COMMITMENT_OPTIONS,
} from './drSecDimQuestionnaire'
import * as Styled from './DrSecDimStyles'

interface DrSecDimQuestionnaireProps {
	initialCustomisation: UserCustomisation | null
	onComplete: (customisation: UserCustomisation) => void
	onCancel: () => void
	// First-run welcome shown as the heading on step 1 only. When set, the flow is
	// entered straight from the empty state instead of a separate landing screen.
	greeting?: string
}

interface SingleChoiceProps {
	question: string
	options: readonly string[]
	value: string
	onSelect: (value: string) => void
	firstOptionDataTour?: string
}

interface MultiChoiceProps {
	question: string
	options: readonly string[]
	values: readonly string[]
	onToggle: (value: string) => void
	firstOptionDataTour?: string
}

// One question rendered as a wrap of single-choice pills. A value is always
// selected (steps seed defaults), so there is no empty state.
function SingleChoice({
	question,
	options,
	value,
	onSelect,
	firstOptionDataTour,
}: SingleChoiceProps) {
	return (
		<Styled.QuestionGroup>
			<Styled.QuestionLabel>{question}</Styled.QuestionLabel>
			<Styled.OptionList role='radiogroup' aria-label={question}>
				{options.map((option, index) => (
					<Styled.OptionButton
						key={option}
						type='button'
						role='radio'
						aria-checked={value === option}
						selected={value === option}
						data-tour={index === 0 ? firstOptionDataTour : undefined}
						onClick={() => onSelect(option)}
					>
						{option}
					</Styled.OptionButton>
				))}
			</Styled.OptionList>
		</Styled.QuestionGroup>
	)
}

// One question rendered as a wrap of toggleable pills; any number can be on.
function MultiChoice({
	question,
	options,
	values,
	onToggle,
	firstOptionDataTour,
}: MultiChoiceProps) {
	return (
		<Styled.QuestionGroup>
			<Styled.QuestionLabel>{question}</Styled.QuestionLabel>
			<Styled.OptionList role='group' aria-label={question}>
				{options.map((option, index) => (
					<Styled.OptionButton
						key={option}
						type='button'
						role='checkbox'
						aria-checked={values.includes(option)}
						selected={values.includes(option)}
						data-tour={index === 0 ? firstOptionDataTour : undefined}
						onClick={() => onToggle(option)}
					>
						{option}
					</Styled.OptionButton>
				))}
			</Styled.OptionList>
		</Styled.QuestionGroup>
	)
}

function DrSecDimQuestionnaire({
	initialCustomisation,
	onComplete,
	onCancel,
	greeting,
}: DrSecDimQuestionnaireProps) {
	const [form, setForm] = useState<UserCustomisation>(() =>
		withDefaults(initialCustomisation),
	)
	const [stepIndex, setStepIndex] = useState(0)

	const setField = <K extends keyof UserCustomisation>(
		key: K,
		value: UserCustomisation[K],
	) => setForm(prev => ({ ...prev, [key]: value }))

	const toggleLanguage = (value: string) => {
		setForm(prev => {
			const current = prev.language ?? []
			const selected = current.includes(value)
				? current.filter(item => item !== value)
				: [...current, value]
			// Keep the option order so a selection is stored the same way
			// regardless of click order.
			return {
				...prev,
				language: LANGUAGE_OPTIONS.filter(option => selected.includes(option)),
			}
		})
	}

	const step = WIZARD_STEPS[stepIndex]
	const onFirst = isFirstStep(stepIndex)
	const onLast = isLastStep(stepIndex)
	const progress = (stepIndex + 1) / WIZARD_STEP_COUNT
	const stepAnswered = isStepAnswered(step.id, form)

	const editing = hasCustomisation(initialCustomisation)
	const baseHeading = editing ? 'Edit preferences' : 'Setup Learning Path'
	// Greet on the entry step, then fall back to the plain title as the learner
	// moves through the flow.
	const heading = greeting && onFirst ? greeting : baseHeading
	const submitLabel = editing ? 'Regenerate pathway' : 'Generate pathway'

	const renderStep = () => {
		switch (step.id) {
			case 'language':
				return (
					<>
						<MultiChoice
							question='Which languages do you work with?'
							options={LANGUAGE_OPTIONS}
							values={form.language ?? []}
							onToggle={toggleLanguage}
							firstOptionDataTour='dr-secdim-language-option'
						/>
						{/* Shown up front rather than as an error after the learner hits
						    a disabled Next, since a customisation saved without a
						    language opens with nothing selected. */}
						<Styled.QuestionHint>
							Pick one or more. You can change this later.
						</Styled.QuestionHint>
					</>
				)
			case 'experience-role':
				return (
					<>
						<SingleChoice
							question="What's your experience level?"
							options={EXPERIENCE_OPTIONS}
							value={form.seniority ?? DEFAULT_CUSTOMISATION.seniority ?? ''}
							onSelect={value => setField('seniority', value)}
						/>
						<SingleChoice
							question='What best describes your role?'
							options={ROLE_OPTIONS}
							value={form.role ?? DEFAULT_CUSTOMISATION.role ?? ''}
							onSelect={value => setField('role', value)}
						/>
					</>
				)
			case 'objective':
				return (
					<SingleChoice
						question="What's your main objective?"
						options={OBJECTIVE_OPTIONS}
						value={form.objective ?? DEFAULT_CUSTOMISATION.objective ?? ''}
						onSelect={value => setField('objective', value)}
					/>
				)
			case 'commitment':
			default:
				return (
					<SingleChoice
						question='How much time can you commit?'
						options={COMMITMENT_OPTIONS}
						value={form.timeAvailable ?? DEFAULT_CUSTOMISATION.timeAvailable ?? ''}
						onSelect={value => setField('timeAvailable', value)}
					/>
				)
		}
	}

	// Cancel belongs to the Edit Preferences flow only — first-time setup enters
	// from the empty state with nothing to cancel back to, so it's hidden there.
	// Within editing it still shows only at the entry (first) and exit (last) steps.
	const showCancel = editing && (onFirst || onLast)

	return (
		<Styled.ContentWrapper data-tour='dr-secdim-questionnaire'>
			<Styled.WizardProgressTrack
				role='progressbar'
				aria-label='Setup progress'
				aria-valuemin={0}
				aria-valuemax={WIZARD_STEP_COUNT}
				aria-valuenow={stepIndex + 1}
			>
				<Styled.WizardProgressBar style={{ width: `${progress * 100}%` }} />
			</Styled.WizardProgressTrack>
			<Styled.WizardContent>
				<Styled.PanelBody>
					<Styled.WizardHeading>{heading}</Styled.WizardHeading>
					<Styled.SectionLabel>
						Step {stepIndex + 1} of {WIZARD_STEP_COUNT} — {step.title}
					</Styled.SectionLabel>
					{/* Keyed by step id so the fade replays on each transition. */}
					<Styled.StepFade key={step.id}>{renderStep()}</Styled.StepFade>
				</Styled.PanelBody>
				<Styled.WizardFooter>
					{showCancel ? (
						<SecDimButton
							variant='outlined'
							sx={Styled.DESTRUCTIVE_OUTLINE_SX}
							onClick={onCancel}
							rounded
						>
							Cancel
						</SecDimButton>
					) : (
						<Box />
					)}
					<Box sx={{ display: 'flex', gap: 1 }}>
						{!onFirst && (
							<SecDimButton
								variant='outlined'
								onClick={() => setStepIndex(prevStep(stepIndex))}
								sx={Styled.SECONDARY_OUTLINE_SX}
								rounded
							>
								Back
							</SecDimButton>
						)}
						{onLast ? (
							<SecDimButton
								rounded
								disabled={!stepAnswered}
								onClick={() => onComplete(finalisePreferences(form))}
							>
								{submitLabel}
							</SecDimButton>
						) : (
							<SecDimButton
								rounded
								disabled={!stepAnswered}
								onClick={() => setStepIndex(nextStep(stepIndex))}
							>
								Next
							</SecDimButton>
						)}
					</Box>
				</Styled.WizardFooter>
			</Styled.WizardContent>
		</Styled.ContentWrapper>
	)
}

export default DrSecDimQuestionnaire
